import React from "react";
import { Dialog, Transition } from "@headlessui/react";
import { PMButton } from "../../../utils/PMButton";
import { CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";

export const ConsumptionSummaryDialog = ({ open, onClose, summaryData }) => {
  if (!summaryData) return null;

  const { used = [], needsHandling = [] } = summaryData;

  return (
    <Transition.Root show={open} as={React.Fragment}>
      <Dialog as="div" className="relative z-[2100]" onClose={onClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900 mb-4">
                  Sammendrag av vareforbruk
                </Dialog.Title>

                <div className="space-y-6">
                  {/* Used products */}
                  {used.length > 0 && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <CheckCircleIcon className="h-5 w-5 text-green-600 mt-0.5" />
                        <div className="ml-3 flex-1">
                          <h4 className="text-sm font-medium text-green-800 mb-2">
                            Produkter brukt på oppdrag ({used.length})
                          </h4>
                          <ul className="space-y-1">
                            {used.map((item, index) => (
                              <li key={index} className="text-sm text-green-700">
                                • {item.product}: {item.quantity} {item.unit}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Products needing handling */}
                  {needsHandling.length > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5" />
                        <div className="ml-3 flex-1">
                          <h4 className="text-sm font-medium text-yellow-800 mb-2">
                            Produkter som må håndteres via varehåndteringsliste ({needsHandling.length})
                          </h4>
                          <p className="text-sm text-yellow-700 mb-2">
                            Disse produktene er ikke fullt ut brukt og må håndteres senere:
                          </p>
                          <ul className="space-y-1">
                            {needsHandling.map((item, index) => (
                              <li key={index} className="text-sm text-yellow-700">
                                • {item.product}: {item.quantity} {item.unit}
                                {item.reason && (
                                  <span className="text-yellow-600 ml-2">({item.reason})</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {used.length === 0 && needsHandling.length === 0 && (
                    <div className="text-center py-4 text-gray-500">
                      Ingen produkter registrert enda
                    </div>
                  )}
                </div>

                <div className="mt-6 flex justify-end">
                  <PMButton onClick={onClose}>
                    Lukk
                  </PMButton>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

